import { parseCellId, type CellId, type ShipState, type Side } from '@brig/rules-engine';

export type Facing = 'east' | 'west';

/** After a move a token faces the way it went east or west; straight north or south keeps `before`. */
export function facingAfterMove(from: CellId, to: CellId, before: Facing = 'east'): Facing {
  const a = parseCellId(from);
  const b = parseCellId(to);
  if (!a || !b || b.x === a.x) return before;
  return b.x < a.x ? 'west' : 'east';
}

/**
 * How each side's ships face before their first move: towards the enemy, i.e. west when the
 * other side's fleet lies, on average, west of this side's.
 */
export function facingTowardEnemy(ships: readonly ShipState[]): Record<Side, Facing> {
  const meanX = (side: Side) => {
    const xs = ships.filter((s) => s.side === side && !s.sunk).map((s) => parseCellId(s.cell)?.x ?? 0);
    return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
  };
  const red = meanX('red');
  const blue = meanX('blue');
  return { red: blue < red ? 'west' : 'east', blue: red < blue ? 'west' : 'east' };
}

/**
 * A ship's heading on the board: the way its bow points, on the map's axes, one of eight
 * (the painted fleet has a view for each, shipArt.ts); a ship keeps the heading of its last move.
 */
export type Heading = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';

/** Each heading's step on the map (x east, y south). */
export const HEADING_VECTOR: Readonly<Record<Heading, { readonly x: number; readonly y: number }>> = {
  N: { x: 0, y: -1 },
  NE: { x: 1, y: -1 },
  E: { x: 1, y: 0 },
  SE: { x: 1, y: 1 },
  S: { x: 0, y: 1 },
  SW: { x: -1, y: 1 },
  W: { x: -1, y: 0 },
  NW: { x: -1, y: -1 },
};

/** Clockwise from east, a heading every 45°. */
const COMPASS: readonly Heading[] = ['E', 'SE', 'S', 'SW', 'W', 'NW', 'N', 'NE'];

/** The heading nearest a way on the map (x east, y south); `fallback` when there is none. */
function nearestHeading(dx: number, dy: number, fallback: Heading): Heading {
  if (dx === 0 && dy === 0) return fallback;
  const k = Math.round(Math.atan2(dy, dx) / (Math.PI / 4));
  return COMPASS[(k + 8) % 8] ?? fallback;
}

/** The DOM's two-way facing for a heading: east-ish bows to the right, the others mirrored. */
export function headingFacing(heading: Heading): Facing {
  const { x } = HEADING_VECTOR[heading];
  return x > 0 || heading === 'N' ? 'east' : 'west';
}

/** The heading after a move from `from` to `to`: the way it went, to the nearest 45°. */
export function headingAfterMove(from: CellId, to: CellId, before: Heading = 'E'): Heading {
  const a = parseCellId(from);
  const b = parseCellId(to);
  if (!a || !b) return before;
  return nearestHeading(b.x - a.x, b.y - a.y, before);
}

/**
 * Each side's heading before its ships first move: at the enemy, the way the other fleet
 * mostly lies, to the nearest 45°.
 */
export function headingTowardEnemy(ships: readonly ShipState[]): Record<Side, Heading> {
  const centre = (side: Side) => {
    const ps = ships
      .filter((s) => s.side === side && !s.sunk)
      .map((s) => parseCellId(s.cell))
      .filter((p) => p !== null);
    const n = ps.length || 1;
    return { x: ps.reduce((t, p) => t + p.x, 0) / n, y: ps.reduce((t, p) => t + p.y, 0) / n };
  };
  const toward = (own: Side, other: Side, fallback: Heading): Heading => {
    const [a, b] = [centre(own), centre(other)];
    return nearestHeading(b.x - a.x, b.y - a.y, fallback);
  };
  return { red: toward('red', 'blue', 'E'), blue: toward('blue', 'red', 'W') };
}
